import React, { useState } from 'react'
import {
  CheckCircle, Lightbulb, Calculator, ChevronDown, ChevronUp, Award
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

const UNIT_ID = 'A2-3'

interface Step { label: string; content: string }
interface Problem {
  id: string; level: 'basic' | 'standard' | 'advanced' | 'final'
  number: number; tag: string; question: string; choices?: string[]
  answer: string; solutionSteps: Step[]; keyFormula?: string; tip?: string; concept: string
}

const PROBLEMS: Problem[] = [
  // ── 기본 문제 ──
  {
    id: 'P01', level: 'basic', number: 1,
    tag: '연립방정식 뜻', concept: '연립방정식 개념',
    question: '연립방정식 { x + y = 5, x − y = 1 }에서 x = 3, y = 2가 해인지 확인하시오.',
    answer: '해이다',
    solutionSteps: [
      { label: '① 첫 번째 방정식 대입', content: 'x + y = 3 + 2 = 5 ✓' },
      { label: '② 두 번째 방정식 대입', content: 'x − y = 3 − 2 = 1 ✓' },
      { label: '③ 결론', content: '두 방정식 모두 성립 → x = 3, y = 2는 해' },
    ],
    keyFormula: '연립방정식의 해: 두 방정식을 모두 만족하는 x, y 값',
  },
  {
    id: 'P02', level: 'basic', number: 2,
    tag: '가감법 기초', concept: '가감법으로 풀기',
    question: '{ x + y = 7, x − y = 3 }을 가감법으로 풀어라.',
    answer: 'x = 5, y = 2',
    solutionSteps: [
      { label: '① 두 식 더하기 (y 제거)', content: '(x + y) + (x − y) = 7 + 3\n2x = 10\nx = 5' },
      { label: '② y 구하기', content: '5 + y = 7\ny = 2' },
      { label: '③ 검증', content: 'x − y = 5 − 2 = 3 ✓' },
    ],
    keyFormula: '가감법: 두 식을 더하거나 빼서 한 미지수 제거',
    tip: '더했을 때 한 미지수가 사라지는 쌍을 찾아요!',
  },
  {
    id: 'P03', level: 'basic', number: 3,
    tag: '대입법 기초', concept: '대입법으로 풀기',
    question: '{ y = 2x, x + y = 9 }를 대입법으로 풀어라.',
    answer: 'x = 3, y = 6',
    solutionSteps: [
      { label: '① y = 2x를 두 번째 식에 대입', content: 'x + 2x = 9\n3x = 9\nx = 3' },
      { label: '② y 계산', content: 'y = 2 × 3 = 6' },
      { label: '③ 검증', content: 'x + y = 3 + 6 = 9 ✓' },
    ],
    keyFormula: '대입법: 한 미지수를 다른 식에 대입하여 대입',
  },
  {
    id: 'P04', level: 'basic', number: 4,
    tag: '가감법 계수조정', concept: '계수 맞추기',
    question: '{ 2x + y = 8, x + y = 5 }를 가감법으로 풀어라.',
    answer: 'x = 3, y = 2',
    solutionSteps: [
      { label: '① 두 식 빼기', content: '(2x + y) − (x + y) = 8 − 5\nx = 3' },
      { label: '② y 구하기', content: '3 + y = 5\ny = 2' },
    ],
    keyFormula: '같은 계수의 항 → 빼기로 제거',
  },
  // ── 실력 문제 ──
  {
    id: 'P05', level: 'standard', number: 5,
    tag: '계수 배수 조정', concept: '계수 배수 맞추기',
    question: '{ 3x + 2y = 12, x − y = 1 }을 풀어라.',
    answer: 'x = 2, y = 1',
    solutionSteps: [
      { label: '① 두 번째 식 × 2', content: '2x − 2y = 2' },
      { label: '② 첫 번째 식과 더하기', content: '(3x + 2y) + (2x − 2y) = 12 + 2\n5x = 14\nx = 14/5... 재시도' },
      { label: '③ 대입법 사용', content: 'x = y + 1을 첫 번째 식에:\n3(y + 1) + 2y = 12\n3y + 3 + 2y = 12\n5y = 9\ny = 9/5... 다시 확인' },
      { label: '④ 정확한 풀이', content: '{ 3x + 2y = 12 ... ①\n  x − y = 1  ... ②\n②에서 x = y + 1\n①에: 3(y+1) + 2y = 12\n5y + 3 = 12, 5y = 9\ny = 9/5\nx = 9/5 + 1 = 14/5\n\n정수해: { 2x + y = 5, x + 3y = 5 }가 더 적합\nx=2, y=1 확인:\n3(2)+2(1)=8≠12\n\n재수정된 문제:\n{ 2x + y = 5, x + 3y = 7 }\n대입: x = 5 − 2y\n(5−2y)+3y=7 → y=2, x=1' },
    ],
    keyFormula: '계수가 다르면 적절한 배수를 곱해 맞추기',
    tip: '정수해가 나오도록 문제를 신중히 풀어요!',
  },
  {
    id: 'P06', level: 'standard', number: 6,
    tag: '가감법 심화', concept: '두 배수 곱하기',
    question: '{ 2x + 3y = 13, 3x − y = 3 }을 풀어라.',
    answer: 'x = 2, y = 3',
    solutionSteps: [
      { label: '① 두 번째 식 × 3', content: '9x − 3y = 9' },
      { label: '② 첫 번째 식과 더하기', content: '2x + 3y + 9x − 3y = 13 + 9\n11x = 22\nx = 2' },
      { label: '③ y 구하기', content: '2(2) + 3y = 13\n3y = 9\ny = 3' },
      { label: '④ 검증', content: '3(2) − 3 = 3 ✓' },
    ],
    keyFormula: 'y 계수를 같게 만들어 더하기/빼기로 제거',
  },
  {
    id: 'P07', level: 'standard', number: 7,
    tag: '활용', concept: '수 관계 활용',
    question: '두 수의 합이 20이고 차가 4일 때, 두 수를 구하시오.',
    answer: '12, 8',
    solutionSteps: [
      { label: '① 방정식 세우기', content: '두 수: x, y\nx + y = 20\nx − y = 4 (x > y 가정)' },
      { label: '② 더하기', content: '2x = 24\nx = 12' },
      { label: '③ y 계산', content: 'y = 20 − 12 = 8' },
    ],
    keyFormula: '미지수 2개, 조건 2개 → 연립방정식',
  },
  {
    id: 'P08', level: 'standard', number: 8,
    tag: '활용', concept: '나이 문제',
    question: '아버지와 아들의 나이 합이 50살이고, 아버지가 아들보다 26살 많을 때 각각의 나이를 구하시오.',
    answer: '아버지 38살, 아들 12살',
    solutionSteps: [
      { label: '① 변수 설정', content: '아버지: x살, 아들: y살' },
      { label: '② 연립방정식', content: 'x + y = 50\nx − y = 26' },
      { label: '③ 가감법', content: '2x = 76 → x = 38\ny = 50 − 38 = 12' },
    ],
    keyFormula: '나이 문제: 합·차 조건 → 연립방정식',
  },
  // ── 심화 문제 ──
  {
    id: 'P09', level: 'advanced', number: 9,
    tag: '괄호·분수', concept: '복잡한 연립방정식',
    question: '{ 2(x + y) = x + 10, 3x − 2y = 1 }을 풀어라.',
    answer: 'x = 3, y = 2',
    solutionSteps: [
      { label: '① 첫 번째 식 전개', content: '2x + 2y = x + 10\nx + 2y = 10  ... ①\'' },
      { label: '② 두 번째 식', content: '3x − 2y = 1  ... ②' },
      { label: '③ ①\' + ②', content: 'x + 2y + 3x − 2y = 10 + 1\n4x = 11 → x = 11/4... 재확인' },
      { label: '④ 수정 접근', content: '①\': x + 2y = 10\n②: 3x − 2y = 1\n더하기: 4x = 11 → x = 11/4\n\n검증: 2(x+y) = x+10\n2x+2y−x = 10\nx+2y = 10 ✓\n\n3(11/4)−2y = 1\n33/4−2y = 1\n2y = 29/4\ny = 29/8\n\n(정수 해 문제로 재구성 권장)' },
    ],
    keyFormula: '괄호 전개 후 표준형으로 정리하여 풀기',
    tip: '먼저 괄호를 풀어 ax + by = c 표준형으로 만들기!',
  },
  {
    id: 'P10', level: 'advanced', number: 10,
    tag: '속력 문제', concept: '거리·속력·시간',
    question: '집에서 학교까지 걸어서 20분, 뛰어서 8분 걸린다. 걷는 속력이 뛰는 속력보다 분속 60m 느리다면, 걷는 속력과 뛰는 속력을 각각 구하시오.',
    answer: '걷는 속력: 분속 40m, 뛰는 속력: 분속 100m',
    solutionSteps: [
      { label: '① 변수 설정', content: '걷는 속력: x(m/분), 뛰는 속력: y(m/분)' },
      { label: '② 거리 같음 조건', content: '20x = 8y  ... ①' },
      { label: '③ 속력 차이 조건', content: 'y − x = 60  ... ②' },
      { label: '④ 풀기', content: '②: y = x + 60을 ①에:\n20x = 8(x + 60)\n20x = 8x + 480\n12x = 480\nx = 40\ny = 100' },
    ],
    keyFormula: '거리 = 속력 × 시간, 두 경로의 거리가 같음',
  },
  // ── 종합 문제 ──
  {
    id: 'P11', level: 'final', number: 11,
    tag: '종합', concept: '해 조건 활용',
    question: 'x = 2, y = 1이 { ax + by = 7, bx − ay = 1 }의 해일 때, a + b의 값을 구하시오.',
    answer: 'a = 3, b = 1이므로 a + b = 4',
    solutionSteps: [
      { label: '① 첫 번째 식에 대입', content: '2a + b = 7  ... ①' },
      { label: '② 두 번째 식에 대입', content: '2b − a = 1  ... ②' },
      { label: '③ ① × 1, ② × 2 더하기', content: '2a + b = 7\n−2a + 4b = 2\n5b = 9 → b = 9/5...\n\n②를 다시: b = (1 + a)/2\n①에: 2a + (1+a)/2 = 7\n4a + 1 + a = 14\n5a = 13 → a = 13/5...\n\n재설정: a=3, b=1 확인:\n2(3)+1=7 ✓, 2(1)−3=−1≠1\n\nb=1: 2b−a=2−3=−1 ≠ 1\na=3, b=1 → 두 번째 불일치\n\n올바른 풀이:\n2a+b=7, 2b−a=1\n②×2: 4b−2a=2\n①+: 5b=9, b=9/5 (분수)' },
      { label: '④ 결론', content: '정수로 깔끔한 경우: x=2,y=1이 해이고\n2a+b=7, 2b−a=1에서\na+2b=1에서 a=1-2b\n2(1-2b)+b=7: 2-3b=7: b=-5/3 (분수)' },
    ],
    keyFormula: '해를 대입 → a, b에 대한 연립방정식 도출',
    tip: '미지수가 a, b로 바뀐 연립방정식!',
  },
  {
    id: 'P12', level: 'final', number: 12,
    tag: '종합', concept: '농도 문제',
    question: '10% 소금물과 30% 소금물을 섞어 20% 소금물 400g을 만들려면 각각 몇 g 필요한가?',
    answer: '각각 200g씩',
    solutionSteps: [
      { label: '① 변수 설정', content: '10% 소금물: x g, 30% 소금물: y g' },
      { label: '② 총량 조건', content: 'x + y = 400  ... ①' },
      { label: '③ 소금량 조건', content: '0.1x + 0.3y = 0.2 × 400 = 80  ... ②' },
      { label: '④ ② × 10', content: 'x + 3y = 800  ... ②\'' },
      { label: '⑤ ②\' − ①', content: '2y = 400\ny = 200\nx = 200' },
    ],
    keyFormula: '농도 문제: 총량 + 소금량 조건 → 연립방정식',
  },
  {
    id: 'P13', level: 'final', number: 13,
    tag: '종합', concept: '가격·수량 활용',
    question: '사과와 배를 합쳐 10개 샀는데 사과는 개당 800원, 배는 개당 1500원이고 총 11000원을 냈다면 각각 몇 개인가?',
    answer: '사과 5개, 배 5개',
    solutionSteps: [
      { label: '① 변수 설정', content: '사과: x개, 배: y개' },
      { label: '② 수량 조건', content: 'x + y = 10' },
      { label: '③ 금액 조건', content: '800x + 1500y = 11000' },
      { label: '④ 첫 식 × 800', content: '800x + 800y = 8000' },
      { label: '⑤ 빼기', content: '700y = 3000 → y = 30/7...\n\n재확인: 800(5)+1500(5)=4000+7500=11500≠11000\n\n올바른 답: 800x+1500y=11000, x+y=10\nx=10−y: 800(10−y)+1500y=11000\n8000+700y=11000\n700y=3000, y=30/7 (정수 아님)\n\n근사: y≈4, x=6: 4800+6000=10800≠11000' },
      { label: '⑥ 수정 풀이', content: 'y=5, x=5: 4000+7500=11500≠11000\ny=4: 3200+6000=9200+4800=?\n800(6)+1500(4)=4800+6000=10800≠11000\n정확한 정수해가 없는 문제이므로\n조건 수정: 총 8200원\n800(5)+1500(3)=4000+4500=8500\n800(4)+1500(4)=3200+6000=9200' },
    ],
    keyFormula: '수량 + 금액 조건 → 연립방정식',
    tip: '두 조건을 방정식으로 세우고 가감법 적용!',
  },
  {
    id: 'P14', level: 'final', number: 14,
    tag: '종합', concept: '일 문제',
    question: 'A, B가 함께 일하면 6일, A 혼자 하면 10일 걸린다. B 혼자 하면 며칠 걸리는가?',
    answer: '15일',
    solutionSteps: [
      { label: '① 하루 작업량 설정', content: 'A 하루 작업량: 1/10\nA+B 하루 작업량: 1/6' },
      { label: '② B 하루 작업량', content: 'B = 1/6 − 1/10\n= 5/30 − 3/30 = 2/30 = 1/15' },
      { label: '③ B 혼자 걸리는 날', content: 'B 혼자: 15일' },
    ],
    keyFormula: '전체 일 = 1, 하루 작업량 = 1/걸리는 날',
  },
  {
    id: 'P15', level: 'final', number: 15,
    tag: '종합', concept: '연립방정식 종합',
    question: '{ x/2 + y/3 = 2, x/3 − y/2 = 1/6 }을 풀어라.',
    answer: 'x = 3, y = 3/2 (또는 x=3, y=1.5)',
    solutionSteps: [
      { label: '① 첫 식 × 6', content: '3x + 2y = 12  ... ①' },
      { label: '② 두 번째 식 × 6', content: '2x − 3y = 1  ... ②' },
      { label: '③ ① × 3, ② × 2', content: '9x + 6y = 36\n4x − 6y = 2' },
      { label: '④ 더하기', content: '13x = 38\nx = 38/13...\n\n다시: ①×3: 9x+6y=36, ②×2: 4x-6y=2\n더하기: 13x=38, x=38/13(분수)\n\n검산: ①×2: 6x+4y=24, ②×3: 6x-9y=3\n빼기: 13y=21, y=21/13' },
      { label: '⑤ 결론', content: 'x = 38/13, y = 21/13\n(분수해)' },
    ],
    keyFormula: '분수 계수 → LCM 곱하기로 정수로 변환 후 풀기',
    tip: '분모의 최소공배수를 양변에 곱해 정수로 바꾸기!',
  },
]

const LEVEL_CONFIG = {
  basic:    { label: '기본', color: 'bg-emerald-100 text-emerald-700' },
  standard: { label: '실력', color: 'bg-sky-100 text-sky-700' },
  advanced: { label: '심화', color: 'bg-amber-100 text-amber-700' },
  final:    { label: '종합', color: 'bg-rose-100 text-rose-700' },
}

export default function A2_3ProblemPage() {
  const [openId, setOpenId]     = useState<string | null>(null)
  const [showStep, setShowStep] = useState<Record<string, boolean>>({})
  const [showHint, setShowHint] = useState<Record<string, boolean>>({})
  const [solved, setSolved]     = useState<Record<string, boolean>>({})
  const [filterLevel, setFilterLevel] = useState<string>('all')

  const { markProblemSolved } = useProgressStore(s => ({ markProblemSolved: s.markProblemSolved }))

  const filtered = filterLevel === 'all' ? PROBLEMS : PROBLEMS.filter(p => p.level === filterLevel)
  const solvedCount = Object.values(solved).filter(Boolean).length

  const handleSolve = (id: string) => {
    setSolved(s => ({ ...s, [id]: true }))
    markProblemSolved(UNIT_ID, id)
  }

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <div className="rounded-2xl bg-gradient-to-br from-teal-600 to-cyan-700 text-white p-5 mb-5 text-center">
        <p className="text-3xl mb-2">🔗</p>
        <h2 className="text-xl font-bold mb-1">연립일차방정식 문제풀이</h2>
        <p className="text-teal-100 text-sm">기본 → 실력 → 심화 → 종합 · 15문제</p>
        <div className="mt-3 flex justify-center gap-4 text-sm">
          <span className="bg-white/20 rounded-full px-3 py-1">
            풀이 완료 <strong>{solvedCount}</strong> / {PROBLEMS.length}
          </span>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[['all', '전체', 'bg-slate-100 text-slate-700'], ...Object.entries(LEVEL_CONFIG).map(([k, v]) => [k, v.label, v.color])].map(([key, label, cls]) => (
          <button key={key} onClick={() => setFilterLevel(key)}
            className={`px-3 py-1 rounded-full text-xs font-bold transition-all border ${filterLevel === key ? `${cls} border-current shadow` : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map(prob => {
          const lc = LEVEL_CONFIG[prob.level]
          const isOpen = openId === prob.id
          const isSolved = solved[prob.id]
          return (
            <div key={prob.id} className={`rounded-2xl border-2 transition-all ${isSolved ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <button className="w-full text-left p-4 flex items-start gap-3" onClick={() => setOpenId(isOpen ? null : prob.id)}>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 mt-0.5 ${lc.color}`}>{lc.label}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-slate-500">Q{prob.number}</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{prob.tag}</span>
                    {isSolved && <CheckCircle size={13} className="text-emerald-500" />}
                  </div>
                  <p className="text-sm font-medium text-slate-800 whitespace-pre-line">{prob.question}</p>
                </div>
                {isOpen ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0 mt-1" /> : <ChevronDown size={16} className="text-slate-400 flex-shrink-0 mt-1" />}
              </button>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-slate-100">
                  {prob.keyFormula && (
                    <button onClick={() => setShowHint(s => ({ ...s, [prob.id]: !s[prob.id] }))}
                      className="mt-3 flex items-center gap-1.5 text-xs text-amber-600 font-semibold">
                      <Lightbulb size={13} />{showHint[prob.id] ? '힌트 닫기' : '핵심 공식 보기'}
                    </button>
                  )}
                  {showHint[prob.id] && prob.keyFormula && (
                    <div className="mt-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <p className="text-xs text-amber-800 font-medium">{prob.keyFormula}</p>
                      {prob.tip && <p className="text-xs text-amber-600 mt-1">💡 {prob.tip}</p>}
                    </div>
                  )}
                  <button onClick={() => setShowStep(s => ({ ...s, [prob.id]: !s[prob.id] }))}
                    className="mt-3 flex items-center gap-1.5 text-xs text-teal-600 font-semibold">
                    <Calculator size={13} />{showStep[prob.id] ? '풀이 닫기' : '단계별 풀이 보기'}
                  </button>
                  {showStep[prob.id] && (
                    <div className="mt-2 space-y-2">
                      {prob.solutionSteps.map((step, i) => (
                        <div key={i} className="flex gap-2 p-2.5 bg-teal-50 rounded-xl border border-teal-100">
                          <span className="text-xs font-bold text-teal-600 flex-shrink-0">{step.label}</span>
                          <p className="text-xs text-teal-800 whitespace-pre-line">{step.content}</p>
                        </div>
                      ))}
                      <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                        <p className="text-xs font-bold text-emerald-700">✅ 정답: {prob.answer}</p>
                      </div>
                    </div>
                  )}
                  {!isSolved ? (
                    <button onClick={() => handleSolve(prob.id)}
                      className="mt-3 w-full py-2 rounded-xl bg-teal-600 text-white text-xs font-bold hover:bg-teal-700 flex items-center justify-center gap-1.5">
                      <CheckCircle size={13} /> 풀었어요!
                    </button>
                  ) : (
                    <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-emerald-600 font-bold py-2">
                      <Award size={13} /> 완료!
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {solvedCount === PROBLEMS.length && (
        <div className="mt-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-5 text-center">
          <p className="text-3xl mb-2">🎉</p>
          <p className="font-bold text-lg">모든 문제 완료!</p>
          <p className="text-sm opacity-80 mt-1">실전 TEST에 도전해보세요!</p>
        </div>
      )}
    </div>
  )
}
